import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Users, UploadSimple, ChartLine, Briefcase, SignOut, CaretDown, UserCircle, IdentificationBadge, UsersThree, Database, Certificate, Tag } from '@phosphor-icons/react';
import { useAuth } from '@/context/AuthContext';

const Navigation = () => {
  const location = useLocation();
  const { user, logout, hasPage } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navItems = [
    { path: '/', label: 'Dashboard', icon: ChartLine, pageKey: 'dashboard' },
    { path: '/upload', label: 'Upload CV', icon: UploadSimple, pageKey: 'upload' },
    { path: '/candidates', label: 'Candidates', icon: Users, pageKey: 'candidates' },
    { path: '/jobs', label: 'Job Descriptions', icon: Briefcase, pageKey: 'jobs' },
  ].filter((item) => hasPage(item.pageKey));

  const adminMenuItems = [
    { path: '/certifications', label: 'Certifications', icon: Certificate, pageKey: 'certifications' },
    { path: '/personas', label: 'Personas', icon: IdentificationBadge, pageKey: 'personas' },
    { path: '/users', label: 'Users', icon: UsersThree, pageKey: 'users' },
    { path: '/master-data', label: 'Master Data', icon: Database, pageKey: 'master_data' },
    { path: '/tags', label: 'Tag Management', icon: Tag, pageKey: 'tags' },
  ].filter((item) => hasPage(item.pageKey));

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()
    : user?.email?.[0]?.toUpperCase() || 'U';

  return (
    <nav
      className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-slate-200"
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-3">
            <img
              src="/aays-logo.webp"
              alt="Aays"
              className="h-8 w-auto"
              data-testid="nav-aays-logo"
            />
            <div className="h-6 w-px bg-white/20" />
            <span className="text-xl font-semibold text-white tracking-tight">
              CV Vault
            </span>
          </div>

          <div className="flex items-center space-x-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-link-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={`
                    flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium
                    transition-all duration-200 ease-out
                    ${isActive ? 'bg-[#002FA7] text-white' : 'text-slate-700 hover:bg-slate-100'}
                  `}
                >
                  <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
                  <span className="hidden md:inline">{item.label}</span>
                </Link>
              );
            })}

            {/* User Menu */}
            <div className="relative ml-2" ref={menuRef}>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="flex items-center space-x-2 pl-2 pr-3 py-1.5 rounded-md hover:bg-slate-100 transition-colors"
                data-testid="user-menu-button"
              >
                <div
                  className="w-8 h-8 rounded-full text-white flex items-center justify-center text-xs font-semibold"
                  style={{ background: 'linear-gradient(135deg, #C82EEB 0%, #7C3AED 100%)' }}
                >
                  {initials}
                </div>
                <CaretDown size={14} className="text-slate-500" weight="bold" />
              </button>

              {menuOpen && (
                <div
                  className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden"
                  data-testid="user-menu-dropdown"
                >
                  <div className="px-4 py-3 border-b border-slate-100">
                    <div className="flex items-center space-x-3">
                      <UserCircle size={32} weight="duotone" className="text-[#002FA7]" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate" data-testid="user-name">
                          {user?.name || 'User'}
                        </p>
                        <p className="text-xs text-slate-500 truncate" data-testid="user-email">
                          {user?.email || ''}
                        </p>
                      </div>
                    </div>
                  </div>
                  {adminMenuItems.length > 0 && (
                    <div className="py-1 border-b border-slate-100">
                      {adminMenuItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setMenuOpen(false)}
                            data-testid={`nav-link-${item.label.toLowerCase().replace(' ', '-')}`}
                            className="w-full flex items-center space-x-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            <Icon size={16} weight="regular" />
                            <span>{item.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                  <button
                    onClick={logout}
                    className="w-full flex items-center space-x-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    data-testid="logout-button"
                  >
                    <SignOut size={16} weight="regular" />
                    <span>Sign out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
